import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

const WishlistButton = ({ productId, size = 'md', showLabel = false }) => {
  const { user } = useAuth();
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  // Check if user is authenticated - either via React state or token
  const token = localStorage.getItem('token');
  const isAuthenticated = !!(user || token);

  useEffect(() => {
    if (productId) {
      if (isAuthenticated) {
        fetchStatus();
      }
      fetchCount();
    }
  }, [productId, isAuthenticated]);

  const fetchStatus = async () => {
    try {
      const res = await fetch(`/api/wishlist/product/${productId}/check`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setIsWishlisted(data.isWishlisted || false);
      }
    } catch (error) {
      console.error('Error fetching wishlist status:', error);
    }
  };

  const fetchCount = async () => {
    try {
      const res = await fetch(`/api/wishlist/product/${productId}/count`);
      if (res.ok) {
        const data = await res.json();
        setCount(data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching wishlist count:', error);
    }
  };

  const toggleWishlist = async () => {
    if (!isAuthenticated) {
      alert('Please login to add items to your wishlist');
      return;
    }

    if (isWishlisted) {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`/api/wishlist/product/${productId}`, { method: 'DELETE', headers: getHeaders() });
        if (res.ok) {
          setIsWishlisted(false);
          setCount(prev => Math.max(0, prev - 1));
        } else {
          const data = await res.json();
          setError(data.message || 'Failed to remove from wishlist');
        }
      } catch (error) {
        console.error('Error removing from wishlist:', error);
        setError('Failed to remove from wishlist. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      setShowNotesModal(true);
    }
  };

  const handleAddWithNotes = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/wishlist/product/${productId}`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ notes })
      });
      if (res.ok) {
        setIsWishlisted(true);
        setCount(prev => prev + 1);
        setShowNotesModal(false);
        setNotes('');
      } else {
        const data = await res.json();
        setError(data.message || 'Failed to add to wishlist');
      }
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      setError('Failed to add to wishlist. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-3 text-base'
  };

  if (!productId) return null;

  return (
    <>
      <button
        onClick={toggleWishlist}
        disabled={loading || !isAuthenticated}
        className={`
          wishlist-button inline-flex items-center gap-1.5 rounded-full transition-all
          ${isWishlisted ? 'bg-yellow-50 text-yellow-600 border-yellow-200' : 'bg-gray-50 text-gray-600 border-gray-200'}
          ${!isAuthenticated ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}
          border ${sizeClasses[size]}
        `}
        aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
        title={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
      >
        <span className="wishlist-icon text-lg">{isWishlisted ? '⭐' : '☆'}</span>
        {showLabel && <span className="wishlist-label">{isWishlisted ? 'Wishlisted' : 'Wishlist'}</span>}
        <span className="wishlist-count text-xs font-medium bg-white/50 px-1.5 py-0.5 rounded-full">
          {count}
        </span>
      </button>

      {showNotesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold mb-2">Add to Wishlist</h3>
            <p className="text-sm text-gray-600 mb-4">
              Add a note to remember why you want this item (optional)
            </p>
            {error && (
              <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm mb-4">
                ❌ {error}
              </div>
            )}
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., 'I want to try this when it's in season'"
              className="w-full border border-gray-300 rounded-lg p-3 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-green-500"
              autoFocus
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => {
                  setShowNotesModal(false);
                  setError('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleAddWithNotes}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
              >
                {loading ? 'Adding...' : 'Add to Wishlist'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WishlistButton;
